import {isBetaDomain, isProdEnvironment} from 'utils/environment';
import {filterAllowedUnicodeCharsFromText} from 'components/BasicEditor/lib/editorUtils';
import moment from 'moment';
import store from 'store';
import {trackEventsService} from 'store/services/eventsServices';
import debounce from 'utils/functions/debounce';
import {AddPrefix} from 'utils/types/remapping';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {BaseEventData, EventData, EventName} from './types';

interface TrackDocumentEventParams {
  eventType: keyof AddPrefix<
    GlobalEventHandlersEventMap & DocumentAndElementEventHandlersEventMap,
    'on'
  >;
  eventName: EventName;
  eventData?: EventData;
  eventCallback(
    event?: ((this: GlobalEventHandlers, ev: UIEvent) => any) | null,
  ): EventData | null;
}

interface UseTrackEventReturn {
  trackEvent(eventName: EventName, payload?: EventData): void;
  debouncedTrackEvent(eventName: EventName, payload?: EventData): void;
  trackDocumentEvent({
    eventType,
    eventName,
    eventData,
  }: TrackDocumentEventParams): void;
}

function replaceNullishValues(key, value) {
  // Converting null and NaN to undefined, which will be later removed from the payload by JSON.stringify()
  if (value === null || Number.isNaN(value)) {
    return undefined;
  }
  return value;
}

function useTrackEvent(): UseTrackEventReturn {
  const state = store.getState();
  const composition = state.getIn(['compositions', 'compositionById']);
  const learningPathUserInstance = state.getIn([
    'learningPaths',
    'learningPath',
  ]);

  function getEventPayload(eventName: EventName, payload?: EventData) {
    switch (eventName) {
      case 'student_login':
        return payload?.auth;

      case 'started_password_recovery':
      case 'finished_password_recovery':
      case 'clicked_on_statistics':
      case 'test_review_not_seen_notification':
      case 'password_reset_attempt':
      case 'feedback_screen_view':
      case 'step1_feedback_screen_view':
      case 'step2_feedback_screen_view':
      case 'click_banner_recompensa':
      case 'click_modal_desistir':
      case 'click_modal_quero_iniciar':
      case 'accessed_contest_result_page_sidebar_student':
      case 'accessed_contest_result_page_banner_student':
      case 'downloaded_contest_certificate_pdf_student':
        return {};

      case 'data_not_found_error':
        return {
          ...payload?.supportingContent,
        };

      case 'has_seen_composition_feedback':
        return {
          ...payload?.review,
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
        };

      case 'has_uncollapsed_review_item':
        return {
          ...payload?.review,
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
          ...payload?.competence,
        };

      case 'scrolled_to_end_of_proposal':
        return {
          ...payload?.composition,
          ...payload?.learningPathUserInstance,
          ...payload?.test,
        };

      case 'time_spent_on_pedagogical_content':
        return {
          ...payload?.pedagogicalContent,
          ...payload?.learningPathUserInstance,
          ...payload?.time,
        };

      case 'time_spent_on_question':
      case 'finished_question_view':
        return {
          ...payload?.learningPathUserInstance,
          ...payload?.question,
          ...payload?.time,
        };

      case 'copied_selected_text_in_question':
      case 'selected_question_text':
        return {
          ...payload?.question,
          ...payload?.learningPathUserInstance,
          ...payload?.supportingContent,
        };

      case 'scrolled_to_end_of_pedagogical_content':
        return {
          ...payload?.pedagogicalContent,
          ...payload?.learningPathUserInstance,
        };

      case 'time_spent_with_open_editor':
      case 'time_spent_on_writing_preparation':
        return {
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
          ...payload?.time,
        };

      case 'has_opened_structure_report_indicator':
      case 'passed_between_structure_and_ia_report':
        return {
          ...payload?.review,
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
        };

      case 'time_spent_on_review_tab':
      case 'time_spent_on_structure_indicators_tab':
        return {
          ...payload?.review,
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
          ...payload?.time,
        };

      case 'pasted_text_in_writing':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.learningPathUserInstance,
          ...payload?.supportingContent,
        };

      case 'copied_selected_text_in_writing':
      case 'selected_proposal_text':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.supportingContent,
          ...payload?.learningPathUserInstance,
        };

      case 'finished_pedagogical_content_view':
      case 'selected_pedagogical_content_text':
      case 'copied_pedagogical_content_text':
        return {
          ...payload?.pedagogicalContent,
          ...payload?.learningPathUserInstance,
          ...payload?.supportingContent,
        };

      case 'started_access_code_signup':
      case 'finished_access_code_signup':
        return {...payload?.accessCode};

      case 'pressed_backspace':
        return {
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
          ...payload?.count,
        };

      case 'selected_annotation_tabs':
        return {
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
        };

      case 'clicked_on_composition_submission':
        return {
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
        };

      case 'confirmed_composition_submission':
        return {
          ...payload?.composition,
          ...payload?.test,
          ...payload?.learningPathUserInstance,
        };

      case 'multigenre_time_spent_in_criteria_review':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.review,
          ...payload?.learningPathInstance,
          ...payload?.time,
          ...payload?.competence,
        };

      case 'multigenre_time_spent_in_general_review':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.review,
          ...payload?.learningPathInstance,
          ...payload?.time,
        };

      case 'multigenre_seen_general_review':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.review,
          ...payload?.learningPathInstance,
          ...payload?.learningPathUserInstance,
        };

      case 'multigenre_clicked_on_composition':
      case 'multigenre_clicked_on_criteria_review':
      case 'multigenre_clicked_on_general_review':
      case 'multigenre_clicked_on_review_emotion':
        return {
          test: {
            test_id: Number(composition?.getIn(['test', 'id'])), // the type of test id is wrong on letrusAPI interfaces, should be number
          },
          composition: {
            composition_id: composition?.get('id'),
          },
          review: {
            review_id: Number(
              composition.get('reviews')?.first()?.get('id') ?? 0,
            ),
          },
          learningPathUserInstance: {
            learning_path_user_instance_id:
              composition.get('learning_path_user_instance_id') ?? 0,
            step_id: composition.get('step_id') ?? 0,
            learning_path_status: learningPathUserInstance?.status ?? '',
          },
        };

      case 'multigenre_seen_criteria_review':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.review,
          ...payload?.learningPathInstance,
          ...payload?.learningPathUserInstance,
          ...payload?.competence,
        };

      case 'multigenre_clicked_on_criteria':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.review,
          ...payload?.time,
          ...payload?.learningPathInstance,
          ...payload?.learningPathUserInstance,
          ...payload?.competence,
        };

      case 'offline_app_sync_sucess':
        return {
          ...payload?.composition,
          ...payload?.pedagogicalContent,
          ...payload?.question,
        };

      case 'offline_app_activity_download':
        return {
          ...payload?.test,
          ...payload?.learningPathInstance,
        };

      case 'test_group_modal_1':
      case 'test_group_modal_2':
      case 'test_group_sidebar_1':
      case 'test_group_sidebar_2':
        return {
          ...payload?.test,
          ...payload?.composition,
        };

      case 'used_ocr_feature':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.count,
          ...payload?.supportingContent,
        };

      case 'has_seen_annotations_review':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.review,
        };

      case 'has_seen_annotations_tooltip':
        return {
          ...payload?.test,
          ...payload?.composition,
          ...payload?.review,
          ...payload?.competence,
        };

      case 'clicked_restore_password':
        return {
          ...payload?.user,
        };

      case 'password_reset_login_success':
        return {
          ...payload?.time,
        };

      case 'step3_feedback_all_competencies_screen_view':
      case 'step3_feedback_competence_button_clicked':
        return {
          ...payload?.composition,
        };

      case 'step3_feedback_competence_expanded':
      case 'step5_feedback_competence_selected':
        return {
          ...payload?.composition,
          ...payload?.competence,
        };

      case 'step5_feedback_content_expanded':
        return {
          ...payload?.composition,
          ...payload?.competence,
        };

      case 'access_structure_indicators_button_clicked':
        return {
          ...payload?.composition,
        };

      case 'feedback_session_duration':
        return {
          ...payload?.composition,
          ...payload?.time,
        };

      case 'composition_save_error':
        return {
          ...payload?.composition,
          ...payload?.test,
          ...payload?.time,
          ...payload?.error,
        };

      case 'clicked_on_recommendation_alert_student':
        return {
          ...payload?.recommendation,
          ...payload?.user,
          ...payload?.network,
          ...payload?.school,
        };

      case 'accessed_messages_page_student':
        return {
          ...payload?.user,
          ...payload?.school,
        };

      case 'clicked_on_message_student':
        return {
          ...payload?.user,
          ...payload?.network,
          ...payload?.school,
        };

      case 'accessed_recommendations_page_student':
        return {
          ...payload?.user,
          ...payload?.school,
        };

      case 'clicked_on_recommendation_student':
        return {
          ...payload?.recommendation,
          ...payload?.user,
          ...payload?.network,
          ...payload?.school,
        };

      default:
        return {};
    }
  }

  function trackEvent(eventName: EventName, payload?: EventData) {
    const {user} = getUserData();

    const baseEventPayload: BaseEventData = {
      event_name: eventName,
      user_id: user?.id,
      event_date: moment().format('YYYYMMDD'),
      event_time: moment().format('HHmmss'),
      user_system_info: navigator.userAgent,
      user_screen_resolution: `${window.screen.height}x${window.screen.width}`,
    };

    const requestPayload = {
      ...baseEventPayload,
      ...getEventPayload(eventName, payload),
    };

    const serializedRequestPayload = JSON.stringify(
      requestPayload,
      replaceNullishValues,
    );

    if (isProdEnvironment && !isBetaDomain) {
      trackEventsService({
        DeliveryStreamName: 'clickstream-prod-stream',
        Record: {
          Data: window.btoa(
            filterAllowedUnicodeCharsFromText(serializedRequestPayload),
          ),
        },
      });
    }
  }

  function trackDocumentEvent({
    eventType,
    eventName,
    eventData,
    eventCallback,
  }: TrackDocumentEventParams) {
    const debouncedEvent = debounce((payload: EventData) => {
      trackEvent(eventName, payload);
    }, 1000);

    document[eventType] = (
      event: ((this: GlobalEventHandlers, ev: UIEvent) => any) | null,
    ) => {
      if (eventCallback(event) !== null) {
        debouncedEvent({...eventData, ...eventCallback(event)});
      }
    };
  }

  const debouncedTrackEvent = debounce(trackEvent, 1000);

  return {
    trackEvent,
    debouncedTrackEvent,
    trackDocumentEvent,
  };
}

export default useTrackEvent;
